/**
 * How server changes land locally, per entity type.
 *
 * Adding an offline entity = add its table (new schema version), a repository,
 * and an entry here. Unknown entity types are skipped (logged in dev): a newer
 * server can sync types an older client cannot store yet; bumping the schema
 * version later should reset that project's cursor to backfill them.
 */
import type { PullChange } from '@/lib/api/types';

import { db } from './db';
import {
  applyFieldChange,
  applyFieldPushResult,
  FIELD_ENTITY_TYPES,
  fieldTables,
  isFieldEntity,
} from './field';
import { applyMemberChange, PROJECT_MEMBER_ENTITY } from './members';
import { applyProjectChange, PROJECT_ENTITY, setProjectVersion } from './projects';

type Applier = {
  apply: (change: PullChange, projectId: string) => Promise<void>;
  setVersion?: (entityId: string, version: number) => Promise<void>;
};

const fieldAppliers: Record<string, Applier> = Object.fromEntries(
  FIELD_ENTITY_TYPES.map((type) => [type, { apply: applyFieldChange }]),
);

const appliers: Record<string, Applier> = {
  [PROJECT_ENTITY]: {
    apply: (change) => applyProjectChange(change.entity_id, change.op, change.version, change.data),
    setVersion: setProjectVersion,
  },
  [PROJECT_MEMBER_ENTITY]: {
    apply: (change, projectId) =>
      applyMemberChange(change.entity_id, change.op, change.version, change.data, projectId),
  },
  ...fieldAppliers,
};

/**
 * Tables any applier may write (for the pull/push transaction scope). The
 * media queue is included because a create merged into an existing record
 * moves its unsent photos along.
 */
export function applierTables() {
  return [db.projects, db.project_members, db.outbox, db.media_queue, ...fieldTables()];
}

export async function applyChange(change: PullChange, projectId: string): Promise<boolean> {
  const applier = appliers[change.entity_type];
  if (!applier) {
    if (import.meta.env.DEV)
      console.debug('[sync] skipping unknown entity type', change.entity_type);
    return false;
  }
  await applier.apply(change, projectId);
  return true;
}

/** The server accepted a queued mutation: record its version (and data, when sent). */
export async function applyPushedEntity(
  entityType: string,
  entityId: string,
  version: number | undefined,
  data: Record<string, unknown> | undefined,
  projectId: string | null,
): Promise<void> {
  if (isFieldEntity(entityType)) {
    await applyFieldPushResult(entityType, entityId, version, data, projectId);
    return;
  }
  if (typeof version === 'number') await appliers[entityType]?.setVersion?.(entityId, version);
}
